import {
  Account,
  GroupSession,
  InboundGroupSession,
  type OlmMessage,
  OlmSession,
  verifySignature,
} from "@rhema/crypto";
import { type Api, ApiError } from "./api";
import {
  blindPin,
  escrowAuthKey,
  escrowProof,
  finishPin,
  fromB64,
  importOperatorKey,
  keyCheck,
  newBackupKey,
  OperatorKeyError,
  openLocal,
  openSession,
  pinWrapKey,
  recoveryCodeKeys,
  sealLocal,
  sealSession,
  toB64,
  unwrapBackupKey,
  unwrapRecoveredKey,
  validPin,
  validRecoveryCode,
  wrapBackupKey,
  wrapForOperator,
} from "./backup";
import type { Content, Payload } from "./content";
import type { Store } from "./storage";
import type { BackupSessionIn, Chat, Device, Envelope, EscrowStatus, ServerMessage } from "./types";
import { parseContent } from "./validate";

// End-to-end encryption, following the Matrix design:
// - every chat's messages are encrypted with a Megolm session owned by the sender's device;
// - the Megolm session key reaches each member device over a one-to-one Olm session;
// - a session rotates when someone leaves, after 100 messages, or after a week;
// - a person's own devices pass each other the sessions they hold, so a new device and the devices of
//   a merged account can read the history (see "Keys between devices" below);
// - every device puts the sessions it holds in the account's encrypted key backup on the server,
//   so history never depends on one device staying signed in (see "Key backup" below).

const ALG = "m.megolm.v1.aes-sha2" as const;
const ROTATE_AFTER_MESSAGES = 100;
const ROTATE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;
const TARGET_ONE_TIME_KEYS = 50;
const DEVICE_CACHE_MS = 60_000;
// Asking for missing keys: a session is asked for again after ten minutes, or sooner when a device
// that may hold it comes online. One round asks for at most 200 sessions, at most every 5 seconds.
const ASK_AGAIN_MS = 10 * 60_000;
const ASK_GAP_MS = 5_000;
const ASK_DELAY_MS = 500;
const MAX_ASKED = 200;
const MAX_WANTED = 500;
const REQUESTS_PER_MESSAGE = 100;
// Keys travel 40 to an Olm message, which keeps each well under the server's 64 KB limit. A new
// device is sent the newest 1,000 sessions of each of its person's other devices.
const KEYS_PER_MESSAGE = 40;
const HISTORY_CAP = 1_000;
// Key backup: sessions go up 100 to a request, a couple of seconds after a device gets them. A
// device missing a key looks in the backup at most every 30 seconds. A device without the backup
// key asks its person's other devices at most every minute, and makes a new key only when it has
// no other device, or none has answered in a day.
const BACKUP_BATCH = 100;
const BACKUP_DELAY_MS = 2_000;
const RESTORE_DELAY_MS = 1_000;
const RESTORE_GAP_MS = 30_000;
const RESTORE_PAGES = 200;
const BACKUP_ASK_GAP_MS = 60_000;
const BACKUP_KEY_WAIT_MS = 24 * 60 * 60 * 1000;
const HOLDER_POLL_MS = 1_000;

type OutboundState = {
  pickle: string;
  createdAt: number;
  count: number;
  sharedWith: string[];
  members: string[];
  /** The message index at which each member's devices were first given the session. */
  sharedAt?: Record<string, number>;
};

type InboundState = {
  pickle: string;
  senderKey: string;
  senderUserId: string;
  senderDeviceId: string;
  /** Sessions this device created: the index each member was first given it at. */
  sharedAt?: Record<string, number>;
  /** Received from another device that held it, rather than from its sender when it was new. */
  forwarded?: boolean;
  /** When this device got the session, so the newest go first to a new device. */
  at?: number;
  /** The backup version this session is backed up under. A better copy arrives without it. */
  backedUp?: string;
};

/** Every Olm payload names both ends, so a message cannot be replayed to another device. */
type OlmEnds = {
  sender_user: string;
  sender_device: string;
  sender_ed25519: string;
  recipient_device: string;
  recipient_ed25519: string;
};

type KeyRequest = { chat_id: string; session_id: string; sender_key: string };

type ForwardedKey = KeyRequest & {
  /** The session exported at the earliest index the forwarding device knows. */
  session_key: string;
  sender_user: string;
  sender_device: string;
};

type OlmBody =
  | { type: "m.room_key"; chat_id: string; session_id: string; session_key: string }
  | { type: "key_request"; requests: KeyRequest[] }
  | { type: "forwarded_key"; keys: ForwardedKey[] }
  // The account's backup keys, by version. Only ever between one person's own devices.
  | { type: "backup_keys"; keys: Record<string, string> }
  | { type: "backup_key_request" }
  | { type: "backup_keys_ack"; versions: string[] };

type OlmPayload = OlmEnds & OlmBody;

/** A session this device could not decrypt with, and who might hold it. */
type Wanted = KeyRequest & {
  sender_user: string | null;
  sender_device: string | null;
  asked: number;
  /** The sending device is gone and the backup was read in full without this session. */
  lost?: boolean;
};

export type CryptoOptions = {
  /** Whether someone is in a chat now. Only members are given a sender's keys. */
  isMember?: (chatId: string, userId: string) => Promise<boolean>;
  /** Called with the chats that got keys from the backup outside `processToDevice`. */
  onRestored?: (chatIds: string[]) => void;
  /**
   * The SHA-256 fingerprint of the operator recovery key, built into the app. With it, every
   * backup key this device holds is also wrapped to that key so Rhema support can help recover
   * the person's history, unless they turned on advanced protection. Without it, nothing is.
   */
  operatorKeyFingerprint?: string;
};

/**
 * Whether signing out would lose messages. It is safe when every session this device holds is in
 * the backup and the backup key can still be reached, through the backup PIN or through another
 * device of the person that has confirmed it holds the key.
 */
export type SignOutCheck =
  | { safe: true }
  | { safe: false; reason: "not_backed_up" | "only_device" | "only_holder" };

export type BackupStatus = {
  /** This device holds the current backup key. */
  hasKey: boolean;
  /** The account has a backup key that this device does not hold yet. */
  waitingForKey: boolean;
  /** A backup PIN protects the current backup key. */
  pin: boolean;
  /** The person was asked to set a backup PIN on this device. */
  pinAsked: boolean;
  otherDevices: number;
  /** After wrong PINs, when the next try is allowed. */
  lockedUntil: string | null;
  /** The person turned on advanced protection, so Rhema support cannot recover their history. */
  advancedProtection: boolean;
  /** Rhema support can help recover the current backup key. */
  supportCanRecover: boolean;
  /** A recovery code from Rhema support is waiting to be used, until this time. */
  recoveryCodeUntil: string | null;
  /** After wrong recovery codes, when the next try is allowed. */
  codeLockedUntil: string | null;
};

export type PinRecovery =
  | { ok: true; chats: string[] }
  | { ok: false; reason: "wrong_pin" | "locked" | "none"; lockedUntil: string | null };

export type CodeRecovery =
  | { ok: true; chats: string[] }
  | { ok: false; reason: "wrong_code" | "locked" | "none" | "invalid"; lockedUntil: string | null };

export class BackupPinError extends Error {}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("timed out")), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

export type Decrypted =
  | { ok: true; content: Content }
  | { ok: false; reason: "missing_key" | "lost" | "invalid" | "replay" | "deleted" | "malformed" };

export class DecryptionError extends Error {}

/** A promise chain so account and session state is only ever changed by one task at a time. */
class Mutex {
  private tail: Promise<unknown> = Promise.resolve();
  run<T>(fn: () => Promise<T>): Promise<T> {
    const next = this.tail.then(fn, fn);
    this.tail = next.catch(() => undefined);
    return next;
  }
}

export class CryptoEngine {
  private account!: Account;
  private pickleKey!: Uint8Array;
  private mutex = new Mutex();
  private deviceCache = new Map<string, { at: number; devices: Device[] }>();
  /** The account each old account id answers as now, or null when it answers as no one. */
  private mergedInto = new Map<string, string | null>();
  private wanted: Map<string, Wanted> | null = null;
  private askTimer: ReturnType<typeof setTimeout> | null = null;
  private lastAsked = 0;
  private askForce = false;
  /** Forwarded keys this device owes other devices, sent at the end of processToDevice. */
  private replies: { device: Device; body: OlmBody }[] = [];
  // Key backup.
  private ring: Record<string, string> | null = null;
  private checks: Record<string, string> = {};
  private currentVersion: string | null = null;
  private needBackupKey = false;
  private ringChanged = false;
  private lastBackupAsk = 0;
  private lastRestore = 0;
  private backupTimer: ReturnType<typeof setTimeout> | null = null;
  private restoreTimer: ReturnType<typeof setTimeout> | null = null;
  /** Some backup key may lack its operator wrap. True on start, so existing keys are backfilled. */
  private operatorDirty = true;
  private operatorRun: Promise<number> | null = null;
  private operatorKey: CryptoKey | null = null;
  private stopped = false;
  deviceId = "";
  curve25519 = "";
  ed25519 = "";

  /**
   * `pickleKey` lets desktop and mobile keep the key that encrypts the stored account in the OS keychain.
   * Without it the key is kept in the store next to the account, as the web app does.
   */
  constructor(
    private readonly api: Api,
    private readonly store: Store,
    readonly userId: string,
    private readonly pickleKeyProvider?: () => Promise<Uint8Array>,
    private readonly options: CryptoOptions = {},
  ) {}

  /** Stops the timer that sends key requests. */
  stop(): void {
    this.stopped = true;
    for (const t of [this.askTimer, this.backupTimer, this.restoreTimer]) if (t) clearTimeout(t);
    this.askTimer = this.backupTimer = this.restoreTimer = null;
  }

  private async loadPickleKey(): Promise<Uint8Array> {
    if (this.pickleKeyProvider) return this.pickleKeyProvider();
    let key = await this.store.get<Uint8Array>("pickle-key");
    if (!key) {
      key = crypto.getRandomValues(new Uint8Array(32));
      await this.store.set("pickle-key", key);
    }
    return key;
  }

  /** Loads this device's account, or creates and registers one. */
  async start(name: string, platform: Device["platform"]): Promise<void> {
    await this.mutex.run(async () => {
      const key = await this.loadPickleKey();
      this.pickleKey = key;
      const pickle = await this.store.get<string>("account");
      this.account = pickle ? Account.unpickle(pickle, key) : Account.create();
      const ids = this.account.identityKeys();
      this.curve25519 = ids.curve25519;
      this.ed25519 = ids.ed25519;

      const server = await this.api.myDevice();
      if (server.device && server.device.curve25519 === ids.curve25519) {
        this.deviceId = server.device.id;
        await this.saveAccount();
        if (server.one_time_key_count < TARGET_ONE_TIME_KEYS / 2) await this.uploadMoreKeys();
        return;
      }
      // A new install, or the server forgot this session's device: register fresh keys.
      this.account.generateOneTimeKeys(TARGET_ONE_TIME_KEYS);
      this.account.generateFallbackKey();
      const device = await this.api.registerDevice({
        name,
        platform,
        curve25519: ids.curve25519,
        ed25519: ids.ed25519,
        one_time_keys: this.signedKeys(this.account.oneTimeKeys()),
        fallback_key: Object.values(this.signedKeys(this.account.fallbackKey()))[0] ?? null,
      });
      this.account.markKeysAsPublished();
      this.deviceId = device.id;
      await this.store.set("device-id", device.id);
      await this.saveAccount();
    });
  }

  private signedKeys(keys: Record<string, string>): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [id, key] of Object.entries(keys)) {
      out[id] = JSON.stringify({ key, signature: this.account.sign(key) });
    }
    return out;
  }

  private async uploadMoreKeys(): Promise<void> {
    this.account.generateOneTimeKeys(TARGET_ONE_TIME_KEYS);
    await this.api.uploadKeys({ one_time_keys: this.signedKeys(this.account.oneTimeKeys()) });
    this.account.markKeysAsPublished();
    await this.saveAccount();
  }

  private async saveAccount(): Promise<void> {
    await this.store.set("account", this.account.pickle(this.pickleKey));
  }

  async devicesOf(userId: string, fresh = false): Promise<Device[]> {
    const hit = this.deviceCache.get(userId);
    if (!fresh && hit && Date.now() - hit.at < DEVICE_CACHE_MS) return hit.devices;
    const devices = await this.api.userDevices(userId);
    this.deviceCache.set(userId, { at: Date.now(), devices });
    // Remember the first keys seen for each device so a later change is noticed.
    for (const d of devices) {
      const known = await this.store.get<string>(`known:${d.id}`);
      if (!known) await this.store.set(`known:${d.id}`, `${d.curve25519}|${d.ed25519}`);
    }
    return devices;
  }

  forgetDevices(userId?: string): void {
    if (userId) {
      this.deviceCache.delete(userId);
      this.mergedInto.delete(userId);
    } else {
      this.deviceCache.clear();
      this.mergedInto.clear();
    }
  }

  // Sending

  encrypt(chat: Chat, content: Content): Promise<Envelope> {
    return this.mutex.run(async () => {
      const memberIds = chat.members.map((m) => m.user.id);
      let out = await this.store.get<OutboundState>(`out:${chat.id}`);
      const someoneLeft = out ? out.members.some((m) => !memberIds.includes(m)) : false;
      const stale =
        !out ||
        someoneLeft ||
        out.count >= ROTATE_AFTER_MESSAGES ||
        Date.now() - out.createdAt > ROTATE_AFTER_MS;
      let group: GroupSession;
      if (stale) {
        group = GroupSession.create();
        out = { pickle: "", createdAt: Date.now(), count: 0, sharedWith: [], members: memberIds };
        // Keep an inbound copy so this device can read its own messages later.
        const inbound = InboundGroupSession.new(group.sessionKey());
        await this.saveInbound(chat.id, inbound, {
          senderKey: this.curve25519,
          senderUserId: this.userId,
          senderDeviceId: this.deviceId,
        });
        inbound.free();
      } else {
        group = GroupSession.unpickle(out!.pickle, this.pickleKey);
      }
      const state = out!;
      state.members = memberIds;

      const targets: Device[] = [];
      for (const uid of memberIds) {
        for (const d of await this.devicesOf(uid)) {
          if (d.id !== this.deviceId && !state.sharedWith.includes(d.id)) targets.push(d);
        }
      }
      if (targets.length) {
        const index = group.messageIndex();
        const shared = await this.shareKey(chat.id, group, targets);
        state.sharedWith.push(...shared);
        // Remember where each member came in, so a request from them later is answered from there
        // and never with what was sent before they were given the session.
        const sharedAt = { ...(state.sharedAt ?? {}) };
        let added = false;
        for (const d of targets) {
          if (!shared.includes(d.id) || sharedAt[d.user_id] !== undefined) continue;
          sharedAt[d.user_id] = index;
          added = true;
        }
        state.sharedAt = sharedAt;
        if (added) {
          const key = `in:${chat.id}:${group.sessionId()}`;
          const mine = await this.store.get<InboundState>(key);
          if (mine) await this.store.set(key, { ...mine, sharedAt });
        }
      }

      const payload: Payload = { chat_id: chat.id, content };
      const ciphertext = group.encrypt(JSON.stringify(payload));
      state.count += 1;
      state.pickle = group.pickle(this.pickleKey);
      const sessionId = group.sessionId();
      group.free();
      await this.store.set(`out:${chat.id}`, state);
      return {
        alg: ALG,
        session_id: sessionId,
        sender_key: this.curve25519,
        device_id: this.deviceId,
        ciphertext,
      };
    });
  }

  /** Forget the outbound session so the next message starts a new one. */
  async rotate(chatId: string): Promise<void> {
    await this.store.delete(`out:${chatId}`);
  }

  private async shareKey(chatId: string, group: GroupSession, devices: Device[]): Promise<string[]> {
    const body: OlmBody = {
      type: "m.room_key",
      chat_id: chatId,
      session_id: group.sessionId(),
      session_key: group.sessionKey(),
    };
    return [...(await this.olmSend(devices.map((device) => ({ device, body }))))];
  }

  /** Encrypts each body for its device over Olm and sends them together. Answers the devices reached. */
  private async olmSend(items: { device: Device; body: OlmBody }[]): Promise<Set<string>> {
    const reached = new Set<string>();
    if (!items.length) return reached;
    const devices = new Map(items.map((i) => [i.device.id, i.device]));
    const sessions = new Map<string, OlmSession>();
    const needKeys: Device[] = [];
    for (const d of devices.values()) {
      const [first, ...rest] = await this.loadOlmSessions(d.curve25519);
      for (const extra of rest) extra.free();
      if (first) sessions.set(d.id, first);
      else needKeys.push(d);
    }
    if (needKeys.length) {
      const claimed = await this.api.claimKeys(needKeys.map((d) => d.id));
      for (const d of needKeys) {
        const claim = claimed[d.id];
        if (!claim) continue;
        const { key, signature } = JSON.parse(claim.key) as { key: string; signature: string };
        if (!verifySignature(d.ed25519, key, signature)) continue;
        sessions.set(d.id, this.account.createOutboundSession(d.curve25519, key));
      }
      await this.saveAccount();
    }

    const messages: { device_id: string; type: string; content: Record<string, unknown> }[] = [];
    for (const { device: d, body } of items) {
      const session = sessions.get(d.id);
      if (!session) continue;
      const payload: OlmPayload = {
        ...body,
        sender_user: this.userId,
        sender_device: this.deviceId,
        sender_ed25519: this.ed25519,
        recipient_device: d.id,
        recipient_ed25519: d.ed25519,
      };
      const message = session.encrypt(JSON.stringify(payload));
      messages.push({
        device_id: d.id,
        type: "m.olm",
        content: { sender_key: this.curve25519, type: message.type, body: message.body },
      });
      reached.add(d.id);
    }
    for (const [id, session] of sessions) {
      const d = devices.get(id);
      if (d) await this.saveOlmSession(d.curve25519, session);
      session.free();
    }
    for (const part of chunk(messages, 1000)) await this.api.sendToDevice(part);
    return reached;
  }

  // Receiving keys

  /** Fetches and applies Olm messages for this device. Returns the chats that got new keys. */
  processToDevice(): Promise<string[]> {
    return this.mutex.run(async () => {
      const chats = new Set<string>();
      let cursor = (await this.store.get<number>("to-device-cursor")) ?? 0;
      for (let page = 0; page < 20; page++) {
        const batch = await this.api.toDevice(cursor);
        for (const m of batch.messages) {
          try {
            for (const chatId of await this.applyOlm(m.sender_user_id, m.sender_device_id, m.content))
              chats.add(chatId);
          } catch {
            // An undecryptable key message is dropped; the sender re-shares on its next rotation.
          }
        }
        cursor = batch.next;
        await this.store.set("to-device-cursor", cursor);
        if (batch.messages.length === 0) break;
      }
      // Acknowledge the last page so the server can delete it.
      if (cursor) await this.api.toDevice(cursor);
      const replies = this.replies;
      this.replies = [];
      await this.olmSend(replies).catch(() => undefined);
      if (this.ringChanged) {
        this.ringChanged = false;
        this.scheduleRestore(0, true);
        this.scheduleBackup();
      }
      if (chats.size) await this.forgetWanted([...chats]);
      const mine = await this.api.myDevice();
      if (mine.one_time_key_count < TARGET_ONE_TIME_KEYS / 2) await this.uploadMoreKeys();
      return [...chats];
    });
  }

  private async applyOlm(senderUser: string, senderDevice: string, content: Record<string, unknown>) {
    const senderKey = String(content.sender_key ?? "");
    const message: OlmMessage = { type: Number(content.type) as 0 | 1, body: String(content.body ?? "") };
    const devices = await this.devicesOf(senderUser);
    let device = devices.find((d) => d.id === senderDevice);
    if (!device) device = (await this.devicesOf(senderUser, true)).find((d) => d.id === senderDevice);
    if (!device || device.curve25519 !== senderKey) throw new DecryptionError("unknown sender device");

    let plaintext: string | null = null;
    for (const session of await this.loadOlmSessions(senderKey)) {
      if (message.type === 0 && !session.matchesPreKey(senderKey, message)) {
        session.free();
        continue;
      }
      try {
        plaintext = session.decrypt(message);
        await this.saveOlmSession(senderKey, session);
        session.free();
        break;
      } catch {
        session.free();
      }
    }
    if (plaintext === null) {
      if (message.type !== 0) throw new DecryptionError("no session");
      const created = this.account.createInboundSession(senderKey, message);
      plaintext = created.plaintext;
      await this.saveOlmSession(senderKey, created.session);
      created.session.free();
      await this.saveAccount();
    }

    const payload = JSON.parse(plaintext) as OlmPayload;
    if (
      payload.recipient_device !== this.deviceId ||
      payload.recipient_ed25519 !== this.ed25519 ||
      payload.sender_user !== senderUser ||
      payload.sender_device !== senderDevice ||
      payload.sender_ed25519 !== device.ed25519
    ) {
      throw new DecryptionError("key message does not match its sender");
    }
    if (payload.type === "key_request") {
      await this.answer(device, senderUser, payload.requests);
      return [];
    }
    if (payload.type === "forwarded_key") return this.acceptForwarded(device, senderUser, payload.keys);
    if (
      payload.type === "backup_keys" ||
      payload.type === "backup_key_request" ||
      payload.type === "backup_keys_ack"
    ) {
      // Backup keys pass only between one person's own devices.
      if (senderUser === this.userId) await this.applyBackupMessage(device, payload);
      return [];
    }
    if (payload.type !== "m.room_key") throw new DecryptionError("unknown key message");
    const inbound = InboundGroupSession.new(payload.session_key);
    if (inbound.sessionId() !== payload.session_id) {
      inbound.free();
      throw new DecryptionError("session id mismatch");
    }
    const existing = await this.store.get<InboundState>(`in:${payload.chat_id}:${payload.session_id}`);
    if (!existing) {
      await this.saveInbound(payload.chat_id, inbound, {
        senderKey,
        senderUserId: senderUser,
        senderDeviceId: senderDevice,
      });
    }
    inbound.free();
    return [payload.chat_id];
  }

  // Keys between devices.
  //
  // A device that cannot read a message asks the person's other devices and the sender's device for
  // its session. A device answers only:
  // - another device of its own person, with the whole session it holds; or
  // - as the session's sender, a device of someone still in the chat, from the index at which that
  //   person was first given the session, so nobody reads what was sent before they could.
  // A forwarded key is taken only from one of this person's own devices or from the session's own
  // sender device. When a new device of this person appears, every device that sees it sends it the
  // sessions it holds, newest first, so a new phone or a merged account's devices read the history
  // without asking. The server only ever relays Olm ciphertext.

  /** Notes a session this device lacks, and asks for it shortly. */
  private async want(message: ServerMessage, env: Envelope): Promise<void> {
    const wanted = await this.loadWanted();
    if (wanted.has(env.session_id)) return;
    wanted.set(env.session_id, {
      chat_id: message.chat_id,
      session_id: env.session_id,
      sender_key: env.sender_key,
      sender_user: message.sender_id,
      sender_device: message.sender_device_id ?? env.device_id ?? null,
      asked: 0,
    });
    while (wanted.size > MAX_WANTED) wanted.delete(wanted.keys().next().value as string);
    this.scheduleAsk(ASK_DELAY_MS);
    // Another device may have backed the session up since this one last looked.
    this.scheduleRestore(RESTORE_DELAY_MS);
  }

  private async loadWanted(): Promise<Map<string, Wanted>> {
    if (!this.wanted) {
      const saved = (await this.store.get<Wanted[]>("wanted")) ?? [];
      this.wanted = new Map(saved.map((w) => [w.session_id, w]));
    }
    return this.wanted;
  }

  private async saveWanted(): Promise<void> {
    if (this.wanted) await this.store.set("wanted", [...this.wanted.values()]);
  }

  private async forgetWanted(chatIds: string[]): Promise<void> {
    const wanted = await this.loadWanted();
    let changed = false;
    for (const [id, w] of wanted) {
      if (!chatIds.includes(w.chat_id)) continue;
      if (await this.store.get(`in:${w.chat_id}:${id}`)) {
        wanted.delete(id);
        changed = true;
      }
    }
    if (changed) await this.saveWanted();
  }

  private scheduleAsk(delay: number): void {
    if (this.askTimer) return;
    this.askTimer = setTimeout(() => {
      this.askTimer = null;
      const force = this.askForce;
      this.askForce = false;
      void this.requestKeys({ force }).catch(() => undefined);
    }, delay);
  }

  /** The sessions this device is still waiting for. */
  async waitingFor(): Promise<string[]> {
    return [...(await this.loadWanted()).keys()];
  }

  /**
   * Asks for the sessions this device is waiting for. With `force`, sessions asked for recently are
   * asked for again, as when a device that may hold them has just come online. Answers how many
   * sessions were asked for.
   */
  requestKeys(opts: { force?: boolean } = {}): Promise<number> {
    return this.mutex.run(async () => {
      const now = Date.now();
      const since = now - this.lastAsked;
      if (since < ASK_GAP_MS) {
        this.askForce ||= Boolean(opts.force);
        this.scheduleAsk(ASK_GAP_MS - since);
        return 0;
      }
      const wanted = await this.loadWanted();
      const due = [...wanted.values()]
        .filter((w) => opts.force || now - w.asked > ASK_AGAIN_MS)
        .slice(0, MAX_ASKED);
      if (!due.length) return 0;
      this.lastAsked = now;
      const lists = new Map<string, { device: Device; requests: KeyRequest[] }>();
      const add = (device: Device, w: Wanted) => {
        const entry = lists.get(device.id) ?? { device, requests: [] };
        entry.requests.push({ chat_id: w.chat_id, session_id: w.session_id, sender_key: w.sender_key });
        lists.set(device.id, entry);
      };
      const mine = (await this.devicesOf(this.userId, true)).filter((d) => d.id !== this.deviceId);
      for (const w of due) {
        for (const d of mine) add(d, w);
        if (!w.sender_user || !w.sender_device || w.sender_user === this.userId) continue;
        const sender = (await this.devicesOf(w.sender_user).catch(() => [])).find(
          (d) => d.id === w.sender_device && d.curve25519 === w.sender_key,
        );
        if (sender) add(sender, w);
      }
      const items: { device: Device; body: OlmBody }[] = [];
      for (const { device, requests } of lists.values())
        for (const part of chunk(requests, REQUESTS_PER_MESSAGE))
          items.push({ device, body: { type: "key_request", requests: part } });
      await this.olmSend(items);
      for (const w of due) w.asked = now;
      await this.saveWanted();
      return due.length;
    });
  }

  /** Answers a key request, under the rules above. Replies are sent once the batch is read. */
  private async answer(device: Device, userId: string, requests: KeyRequest[]): Promise<void> {
    const own = userId === this.userId;
    const keys: ForwardedKey[] = [];
    for (const r of (Array.isArray(requests) ? requests : []).slice(0, MAX_ASKED)) {
      const state = await this.store.get<InboundState>(`in:${r.chat_id}:${r.session_id}`);
      if (!state || state.senderKey !== r.sender_key) continue;
      const inbound = InboundGroupSession.unpickle(state.pickle, this.pickleKey);
      try {
        let from: number | undefined;
        if (own) from = inbound.firstKnownIndex();
        else if (state.senderDeviceId === this.deviceId && state.sharedAt?.[userId] !== undefined) {
          const isMember = this.options.isMember ?? ((c, u) => this.isMember(c, u));
          if (await isMember(r.chat_id, userId))
            from = Math.max(inbound.firstKnownIndex(), state.sharedAt[userId]);
        }
        if (from === undefined) continue;
        const exported = inbound.exportAt(from);
        if (!exported) continue;
        keys.push({
          chat_id: r.chat_id,
          session_id: r.session_id,
          sender_key: state.senderKey,
          session_key: exported,
          sender_user: state.senderUserId,
          sender_device: state.senderDeviceId,
        });
      } finally {
        inbound.free();
      }
    }
    for (const part of chunk(keys, KEYS_PER_MESSAGE))
      this.replies.push({ device, body: { type: "forwarded_key", keys: part } });
  }

  private async isMember(chatId: string, userId: string): Promise<boolean> {
    try {
      return (await this.api.chat(chatId)).members.some((m) => m.user.id === userId);
    } catch {
      return false;
    }
  }

  /** Takes forwarded sessions from one of this person's devices or from the session's sender. */
  private async acceptForwarded(device: Device, userId: string, keys: ForwardedKey[]): Promise<string[]> {
    const own = userId === this.userId;
    const chats = new Set<string>();
    for (const k of (Array.isArray(keys) ? keys : []).slice(0, HISTORY_CAP)) {
      const fromSender = device.id === k.sender_device && device.curve25519 === k.sender_key;
      if (!own && !fromSender) continue;
      let inbound: InboundGroupSession;
      try {
        inbound = InboundGroupSession.import(k.session_key);
      } catch {
        continue;
      }
      try {
        if (inbound.sessionId() !== k.session_id) continue;
        const key = `in:${k.chat_id}:${k.session_id}`;
        const existing = await this.store.get<InboundState>(key);
        if (existing) {
          if (existing.senderKey !== k.sender_key) continue;
          const held = InboundGroupSession.unpickle(existing.pickle, this.pickleKey);
          const better = inbound.firstKnownIndex() < held.firstKnownIndex();
          held.free();
          if (!better) continue;
        }
        await this.saveInbound(k.chat_id, inbound, {
          senderKey: k.sender_key,
          // A sender's own device is believed about itself only as far as the server vouches for it.
          senderUserId: own ? k.sender_user : userId,
          senderDeviceId: k.sender_device,
          forwarded: true,
          ...(existing?.sharedAt ? { sharedAt: existing.sharedAt } : {}),
        });
        chats.add(k.chat_id);
      } finally {
        inbound.free();
      }
    }
    return [...chats];
  }

  /**
   * Sends every session this device holds to this person's devices it has not seen before, such as a
   * new phone or the devices of an account merged into this one. On the first run it counts devices
   * newer than this one as new. Answers how many devices were sent history.
   */
  shareWithNewOwnDevices(): Promise<number> {
    return this.mutex.run(async () => {
      const all = await this.devicesOf(this.userId, true);
      const others = all.filter((d) => d.id !== this.deviceId);
      const known = await this.store.get<string[]>("own-devices");
      const me = all.find((d) => d.id === this.deviceId);
      const fresh = known
        ? others.filter((d) => !known.includes(d.id))
        : others.filter((d) => me && d.created_at > me.created_at);
      await this.store.set(
        "own-devices",
        others.map((d) => d.id),
      );
      if (!fresh.length) return 0;
      const keys = await this.historyKeys();
      const ring = await this.loadRing();
      const items: { device: Device; body: OlmBody }[] = [];
      for (const device of fresh) {
        // The backup keys first, so the new device can read everything the backup holds.
        if (Object.keys(ring).length) items.push({ device, body: { type: "backup_keys", keys: ring } });
        for (const part of chunk(keys, KEYS_PER_MESSAGE))
          items.push({ device, body: { type: "forwarded_key", keys: part } });
      }
      await this.olmSend(items);
      return fresh.length;
    });
  }

  /** The sessions this device holds, newest first, each exported at its earliest known index. */
  private async historyKeys(): Promise<ForwardedKey[]> {
    const held: { chatId: string; sessionId: string; state: InboundState }[] = [];
    for (const key of await this.store.keys("in:")) {
      const [, chatId, sessionId] = key.split(":");
      const state = await this.store.get<InboundState>(key);
      if (chatId && sessionId && state) held.push({ chatId, sessionId, state });
    }
    held.sort((a, b) => (b.state.at ?? 0) - (a.state.at ?? 0));
    const keys: ForwardedKey[] = [];
    for (const { chatId, sessionId, state } of held.slice(0, HISTORY_CAP)) {
      const inbound = InboundGroupSession.unpickle(state.pickle, this.pickleKey);
      const exported = inbound.exportAt(inbound.firstKnownIndex());
      inbound.free();
      if (!exported) continue;
      keys.push({
        chat_id: chatId,
        session_id: sessionId,
        sender_key: state.senderKey,
        session_key: exported,
        sender_user: state.senderUserId,
        sender_device: state.senderDeviceId,
      });
    }
    return keys;
  }

  // Key backup.
  //
  // Every device puts each Megolm session it holds in the account's backup on the server, sealed
  // with the account's backup key (see backup.ts), and a device missing a key looks there. The
  // first device that needs a backup key makes one. The key passes only between the person's own
  // devices over Olm: to a new device without being asked, and to any of them that asks. A device
  // that gets a key confirms it, so a device knows whether another one holds the key before it
  // signs out. A backup PIN can also wrap the key (see "Backup PIN" below). The server sees only
  // ciphertext and a check value for each key.

  private async loadRing(): Promise<Record<string, string>> {
    if (!this.ring) {
      const sealed = await this.store.get<string>("backup-keyring");
      this.ring = {};
      if (sealed) {
        try {
          this.ring = await openLocal<Record<string, string>>(this.pickleKey, sealed);
        } catch {
          // Unreadable with this pickle key. Ask the person's other devices again.
        }
      }
    }
    return this.ring;
  }

  private async saveRing(ring: Record<string, string>): Promise<void> {
    this.ring = ring;
    this.operatorDirty = true;
    await this.store.set("backup-keyring", await sealLocal(this.pickleKey, ring));
  }

  /** Takes backup keys whose check matches one of this account's versions. Answers those added. */
  private async addKeys(keys: Record<string, string>): Promise<string[]> {
    const ring = { ...(await this.loadRing()) };
    const fresh = Object.entries(keys ?? {}).filter(
      ([id, key]) => typeof key === "string" && ring[id] !== key,
    );
    if (!fresh.length) return [];
    if (fresh.some(([id]) => !this.checks[id])) await this.refreshChecks();
    const added: string[] = [];
    for (const [id, key] of fresh) {
      try {
        if (this.checks[id] && (await keyCheck(fromB64(key))) === this.checks[id]) {
          ring[id] = key;
          added.push(id);
        }
      } catch {
        // Not a key.
      }
    }
    if (!added.length) return [];
    await this.saveRing(ring);
    // Rows under the new keys may sit behind the cursor, so the next restore reads from the start.
    await this.store.set("backup-cursor", 0);
    if (this.currentVersion && added.includes(this.currentVersion)) this.needBackupKey = false;
    this.ringChanged = true;
    return added;
  }

  private async refreshChecks(): Promise<string | null> {
    const state = await this.api.keyBackup();
    this.checks = Object.fromEntries(state.versions.map((v) => [v.id, v.check]));
    this.currentVersion = state.current?.id ?? null;
    return this.currentVersion;
  }

  /**
   * The backup version this device can upload to now, making the account's first backup key
   * when it has none. Null while this device waits for the key from another of its devices.
   * Runs inside the mutex.
   */
  private async backupVersion(): Promise<string | null> {
    const current = await this.refreshChecks();
    const ring = await this.loadRing();
    if (current && ring[current]) {
      this.needBackupKey = false;
      await this.store.delete("backup-wait-since");
      return current;
    }
    const others = (await this.devicesOf(this.userId, true)).filter((d) => d.id !== this.deviceId);
    if (current) {
      // The backup PIN can bring this key back, so the person is asked for it instead.
      const escrow = await this.api.escrowStatus().catch(() => null);
      if (escrow?.versions.includes(current)) {
        this.needBackupKey = true;
        if (others.length) await this.askForBackupKey(others);
        return null;
      }
      const since = (await this.store.get<number>("backup-wait-since")) ?? Date.now();
      await this.store.set("backup-wait-since", since);
      if (others.length && Date.now() - since < BACKUP_KEY_WAIT_MS) {
        this.needBackupKey = true;
        await this.askForBackupKey(others);
        return null;
      }
    }
    // No backup yet, or the key went with every device that held it: make a new one. Older
    // versions stay readable by any device that still holds their keys.
    const key = newBackupKey();
    const id = crypto.randomUUID();
    const check = await keyCheck(key);
    await this.saveRing({ ...ring, [id]: toB64(key) });
    try {
      await this.api.newKeyBackup({ id, check, replaces: current });
    } catch (err) {
      // Another device made one first. Its key comes over Olm.
      if (err instanceof ApiError && err.status === 409) {
        await this.refreshChecks();
        this.needBackupKey = true;
        if (others.length) await this.askForBackupKey(others, true);
        return null;
      }
      throw err;
    }
    this.checks[id] = check;
    this.currentVersion = id;
    this.needBackupKey = false;
    await this.store.delete("backup-wait-since");
    if (others.length) {
      const keys = await this.loadRing();
      await this.olmSend(others.map((device) => ({ device, body: { type: "backup_keys" as const, keys } })));
    }
    return id;
  }

  private async askForBackupKey(devices: Device[], force = false): Promise<void> {
    if (!force && Date.now() - this.lastBackupAsk < BACKUP_ASK_GAP_MS) return;
    this.lastBackupAsk = Date.now();
    await this.olmSend(devices.map((device) => ({ device, body: { type: "backup_key_request" as const } })));
  }

  /** Asks this person's other devices again for the backup key, if this device still needs it. */
  askForBackupKeyAgain(): Promise<void> {
    return this.mutex.run(async () => {
      if (!this.needBackupKey) return;
      const others = (await this.devicesOf(this.userId, true)).filter((d) => d.id !== this.deviceId);
      if (others.length) await this.askForBackupKey(others, true);
    });
  }

  private async applyBackupMessage(
    device: Device,
    payload: Extract<OlmBody, { type: "backup_keys" | "backup_key_request" | "backup_keys_ack" }>,
  ): Promise<void> {
    if (payload.type === "backup_key_request") {
      const ring = await this.loadRing();
      if (Object.keys(ring).length) this.replies.push({ device, body: { type: "backup_keys", keys: ring } });
      return;
    }
    if (payload.type === "backup_keys_ack") {
      await this.addHolder(device.id, Array.isArray(payload.versions) ? payload.versions : []);
      return;
    }
    await this.addKeys(payload.keys);
    const ring = await this.loadRing();
    const held = Object.keys(payload.keys ?? {}).filter((id) => ring[id] === payload.keys[id]);
    // The sender holds these keys, and now this device does too.
    await this.addHolder(device.id, held);
    if (held.length) this.replies.push({ device, body: { type: "backup_keys_ack", versions: held } });
  }

  private async addHolder(deviceId: string, versions: string[]): Promise<void> {
    if (!versions.length) return;
    const holders = (await this.store.get<Record<string, string[]>>("backup-holders")) ?? {};
    for (const v of versions.filter((v) => typeof v === "string")) {
      const list = new Set(holders[v] ?? []);
      list.add(deviceId);
      holders[v] = [...list];
    }
    await this.store.set("backup-holders", holders);
  }

  private scheduleBackup(): void {
    if (this.stopped || this.backupTimer) return;
    this.backupTimer = setTimeout(() => {
      this.backupTimer = null;
      void this.backupNow().catch(() => undefined);
    }, BACKUP_DELAY_MS);
  }

  private scheduleRestore(delay: number, force = false): void {
    if (this.stopped || this.restoreTimer) return;
    if (!force && Date.now() - this.lastRestore < RESTORE_GAP_MS) return;
    this.restoreTimer = setTimeout(() => {
      this.restoreTimer = null;
      void this.restoreBackup()
        .then((chats) => {
          if (chats.length) this.options.onRestored?.(chats);
        })
        .catch(() => undefined);
    }, delay);
  }

  /** Brings this device's backup up to date: the key, what it lacks and what it holds. */
  async syncBackup(): Promise<string[]> {
    await this.mutex.run(() => this.backupVersion());
    const chats = await this.restoreBackup();
    await this.backupNow();
    return chats;
  }

  /** Reads the backup again from the start, as after a merge brought in more of it. */
  async resetBackupCursor(): Promise<void> {
    await this.store.set("backup-cursor", 0);
  }

  /** The `in:` keys of sessions not yet backed up under `version`. */
  private async pendingKeys(version: string | null): Promise<string[]> {
    const pending: string[] = [];
    for (const key of await this.store.keys("in:")) {
      const state = await this.store.get<InboundState>(key);
      if (state && (!version || state.backedUp !== version)) pending.push(key);
    }
    return pending;
  }

  /**
   * Uploads every session this device holds that the backup does not have under the current key.
   * Answers how many are still not backed up, which is all of them while this device waits for
   * the backup key.
   */
  async backupNow(): Promise<number> {
    const version = await this.mutex.run(() => this.backupVersion());
    // A key this device made or learned since the last look gets its operator wrap.
    if (this.operatorDirty) await this.ensureOperatorWraps().catch(() => undefined);
    const pending = await this.mutex.run(() => this.pendingKeys(version));
    if (!version) return pending.length;
    let left = pending.length;
    for (const part of chunk(pending, BACKUP_BATCH)) {
      if (this.stopped) break;
      await this.mutex.run(async () => {
        const keyB64 = (await this.loadRing())[version];
        if (!keyB64) throw new Error("backup key missing");
        const key = fromB64(keyB64);
        const sessions: BackupSessionIn[] = [];
        const done: [string, InboundState][] = [];
        for (const k of part) {
          const state = await this.store.get<InboundState>(k);
          if (!state || state.backedUp === version) continue;
          const [, chatId, sessionId] = k.split(":");
          if (!chatId || !sessionId) continue;
          const inbound = InboundGroupSession.unpickle(state.pickle, this.pickleKey);
          const first = inbound.firstKnownIndex();
          const exported = inbound.exportAt(first);
          inbound.free();
          if (!exported) continue;
          sessions.push({
            chat_id: chatId,
            session_id: sessionId,
            first_index: first,
            data: await sealSession(key, version, chatId, sessionId, {
              session_key: exported,
              sender_key: state.senderKey,
              sender_user: state.senderUserId,
              sender_device: state.senderDeviceId,
            }),
          });
          done.push([k, state]);
        }
        if (sessions.length) await this.api.putKeyBackup(version, sessions);
        for (const [k, state] of done) await this.store.set(k, { ...state, backedUp: version });
      });
      left -= part.length;
    }
    return left;
  }

  /**
   * Reads the backup from where this device left off and takes every session it can open that
   * reaches further back than what it holds. Answers the chats that got keys.
   */
  async restoreBackup(): Promise<string[]> {
    this.lastRestore = Date.now();
    const chats = new Set<string>();
    let readAll = false;
    for (let page = 0; page < RESTORE_PAGES && !this.stopped; page++) {
      const more = await this.mutex.run(async () => {
        const ring = await this.loadRing();
        if (!Object.keys(ring).length) return false;
        const cursor = (await this.store.get<number>("backup-cursor")) ?? 0;
        const res = await this.api.keyBackupSessions(cursor);
        for (const row of res.sessions) {
          const keyB64 = ring[row.version];
          if (!keyB64) continue;
          if (await this.importRow(row.chat_id, row.session_id, row.version, keyB64, row.data))
            chats.add(row.chat_id);
        }
        await this.store.set("backup-cursor", res.next);
        return res.more;
      });
      if (!more) {
        readAll = true;
        break;
      }
    }
    if (chats.size) await this.mutex.run(() => this.forgetWanted([...chats]));
    if (readAll) for (const chat of await this.markLost()) chats.add(chat);
    return [...chats];
  }

  /**
   * Marks the sessions no one can bring back: the device that sent them is signed out and the
   * backup, read in full under every key the account has, does not hold them. Their messages say
   * so instead of waiting. A key that turns up later is still taken. Answers the chats changed.
   */
  private async markLost(): Promise<string[]> {
    const ring = await this.mutex.run(() => this.loadRing());
    const versions = Object.keys(this.checks);
    if (!versions.length || versions.some((v) => !ring[v])) return [];
    const wanted = await this.mutex.run(() => this.loadWanted());
    const gone: Wanted[] = [];
    for (const w of [...wanted.values()]) {
      if (w.lost || !w.sender_user || !w.sender_device) continue;
      if (await this.store.get(`in:${w.chat_id}:${w.session_id}`)) continue;
      let devices: Device[];
      try {
        devices = await this.devicesOf(w.sender_user, true);
      } catch {
        return [];
      }
      if (!devices.some((d) => d.id === w.sender_device)) gone.push(w);
    }
    if (!gone.length) return [];
    await this.mutex.run(async () => {
      for (const w of gone) if (wanted.has(w.session_id)) w.lost = true;
      await this.saveWanted();
    });
    return [...new Set(gone.map((w) => w.chat_id))];
  }

  /** Opens one backup row and keeps its session if it reaches further back. Inside the mutex. */
  private async importRow(
    chatId: string,
    sessionId: string,
    version: string,
    keyB64: string,
    data: string,
  ): Promise<boolean> {
    let plain: Awaited<ReturnType<typeof openSession>>;
    try {
      // The chat, session and version ids are authenticated, so a row the server moved or
      // made up does not open.
      plain = await openSession(fromB64(keyB64), version, chatId, sessionId, data);
    } catch {
      return false;
    }
    let inbound: InboundGroupSession;
    try {
      inbound = InboundGroupSession.import(plain.session_key);
    } catch {
      return false;
    }
    try {
      if (inbound.sessionId() !== sessionId) return false;
      const existing = await this.store.get<InboundState>(`in:${chatId}:${sessionId}`);
      if (existing) {
        if (existing.senderKey !== plain.sender_key) return false;
        const held = InboundGroupSession.unpickle(existing.pickle, this.pickleKey);
        const better = inbound.firstKnownIndex() < held.firstKnownIndex();
        held.free();
        if (!better) return false;
      }
      // Only this person's devices hold the backup key, so the row is as trusted as a session
      // forwarded by one of them.
      await this.saveInbound(chatId, inbound, {
        senderKey: plain.sender_key,
        senderUserId: plain.sender_user,
        senderDeviceId: plain.sender_device,
        forwarded: true,
        ...(existing?.sharedAt ? { sharedAt: existing.sharedAt } : {}),
        ...(version === this.currentVersion ? { backedUp: version } : {}),
      });
      return true;
    } finally {
      inbound.free();
    }
  }

  /**
   * Imports the sessions of an older store on this device, such as one kept under an account
   * that was merged into this one, whose pickle key is still on the device. The old store is
   * left as it is. Answers the chats that got keys.
   */
  importSessionsFrom(store: Store, pickleKey: Uint8Array, from: string): Promise<string[]> {
    return this.mutex.run(async () => {
      const mark = `imported:${from}`;
      if (await this.store.get(mark)) return [];
      const chats = new Set<string>();
      for (const key of await store.keys("in:")) {
        const state = await store.get<InboundState>(key);
        const [, chatId, sessionId] = key.split(":");
        if (!state || !chatId || !sessionId) continue;
        let old: InboundGroupSession;
        try {
          old = InboundGroupSession.unpickle(state.pickle, pickleKey);
        } catch {
          continue;
        }
        const exported = old.exportAt(old.firstKnownIndex());
        old.free();
        if (!exported) continue;
        const inbound = InboundGroupSession.import(exported);
        try {
          const existing = await this.store.get<InboundState>(key);
          if (existing) {
            if (existing.senderKey !== state.senderKey) continue;
            const held = InboundGroupSession.unpickle(existing.pickle, this.pickleKey);
            const better = inbound.firstKnownIndex() < held.firstKnownIndex();
            held.free();
            if (!better) continue;
          }
          await this.saveInbound(chatId, inbound, {
            senderKey: state.senderKey,
            senderUserId: state.senderUserId,
            senderDeviceId: state.senderDeviceId,
            forwarded: true,
            ...(existing?.sharedAt ? { sharedAt: existing.sharedAt } : {}),
          });
          chats.add(chatId);
        } finally {
          inbound.free();
        }
      }
      // The old store's backup keys too, such as a merged account's.
      const sealed = await store.get<string>("backup-keyring");
      if (sealed) {
        try {
          await this.addKeys(await openLocal<Record<string, string>>(pickleKey, sealed));
        } catch {
          // Unreadable. The keys come from the person's devices instead.
        }
      }
      await this.store.set(mark, Date.now());
      return [...chats];
    });
  }

  /**
   * Backs up everything this device holds and says whether signing out now would leave messages
   * that nothing else can read. Waits at most `timeoutMs`.
   */
  async signOutCheck(timeoutMs = 10_000): Promise<SignOutCheck> {
    const deadline = Date.now() + timeoutMs;
    let left: number;
    try {
      left = await withTimeout(this.backupNow(), timeoutMs);
    } catch {
      return { safe: false, reason: "not_backed_up" };
    }
    const version = this.currentVersion;
    if (left > 0 || !version) return { safe: false, reason: "not_backed_up" };
    // A backup PIN can bring the key back on any device.
    const status = await this.api.escrowStatus().catch(() => null);
    if (status?.versions.includes(version)) return { safe: true };
    let others: Device[];
    try {
      others = (await this.devicesOf(this.userId, true)).filter((d) => d.id !== this.deviceId);
    } catch {
      return { safe: false, reason: "not_backed_up" };
    }
    if (!others.length) return { safe: false, reason: "only_device" };
    const holds = async () => {
      const holders = (await this.store.get<Record<string, string[]>>("backup-holders"))?.[version] ?? [];
      return others.some((d) => holders.includes(d.id));
    };
    if (await holds()) return { safe: true };
    // Give the key to the other devices once more and wait a little for one to confirm it.
    await this.mutex
      .run(async () => {
        const keys = await this.loadRing();
        await this.olmSend(
          others.map((device) => ({ device, body: { type: "backup_keys" as const, keys } })),
        );
      })
      .catch(() => undefined);
    while (Date.now() + HOLDER_POLL_MS < deadline) {
      await new Promise((r) => setTimeout(r, HOLDER_POLL_MS));
      await this.processToDevice().catch(() => undefined);
      if (await holds()) return { safe: true };
    }
    return { safe: false, reason: "only_holder" };
  }

  /** What the person needs to know about their backup on this device. */
  async backupStatus(): Promise<BackupStatus> {
    const current = await this.refreshChecks();
    const ring = await this.loadRing();
    const escrow: EscrowStatus = await this.api
      .escrowStatus()
      .catch(() => ({ versions: [], attempts: 0, locked_until: null }));
    const others = (await this.devicesOf(this.userId, true).catch(() => [])).filter(
      (d) => d.id !== this.deviceId,
    );
    const operator = await this.api.operatorStatus().catch(() => null);
    const code = await this.api.recoveryState().catch(() => null);
    return {
      hasKey: Boolean(current && ring[current]),
      waitingForKey: Boolean(current && !ring[current]),
      pin: Boolean(current && escrow.versions.includes(current)),
      pinAsked: Boolean(await this.store.get("backup-pin-asked")),
      otherDevices: others.length,
      lockedUntil: escrow.locked_until,
      advancedProtection: operator?.advanced_protection ?? false,
      supportCanRecover: Boolean(
        current && operator && !operator.advanced_protection && operator.versions.includes(current),
      ),
      recoveryCodeUntil: code?.expires_at ?? null,
      codeLockedUntil: code?.locked_until ?? null,
    };
  }

  // Recovery through Rhema support.
  //
  // Every backup key this device holds is also wrapped to the operator recovery key in Cloud KMS,
  // after checking that the key the server serves has the fingerprint built into the app. Only
  // KMS can open the wrap, and only for the owner's staff tool, which turns it into a one-time
  // recovery code for the person. Advanced protection turns all of this off.

  /** Wraps every backup key this device holds that has no operator wrap yet. Answers how many. */
  ensureOperatorWraps(): Promise<number> {
    if (!this.operatorRun)
      this.operatorRun = this.wrapForOperator().finally(() => {
        this.operatorRun = null;
      });
    return this.operatorRun;
  }

  private async wrapForOperator(): Promise<number> {
    const pinned = this.options.operatorKeyFingerprint?.toLowerCase();
    if (!pinned) return 0;
    this.operatorDirty = false;
    try {
      const status = await this.api.operatorStatus();
      if (status.advanced_protection) return 0;
      // The server names a key the app does not trust. Wrap nothing.
      if (status.fingerprint !== pinned) throw new OperatorKeyError("The server's recovery key changed.");
      const ring = await this.loadRing();
      if (Object.keys(ring).some((id) => !this.checks[id])) await this.refreshChecks();
      const missing = Object.keys(ring).filter((id) => this.checks[id] && !status.versions.includes(id));
      if (!missing.length) return 0;
      if (!this.operatorKey) {
        const served = await this.api.operatorPublicKey();
        this.operatorKey = await importOperatorKey(served.pem, pinned);
      }
      const wraps = [];
      for (const id of missing) {
        const key = ring[id];
        if (key)
          wraps.push({ version: id, wrapped: await wrapForOperator(this.operatorKey, id, fromB64(key)) });
      }
      for (const part of chunk(wraps, 20)) await this.api.putOperatorWraps(pinned, part);
      return wraps.length;
    } catch (err) {
      this.operatorDirty = true;
      throw err;
    }
  }

  /**
   * Turns advanced protection on or off. On, the server removes the operator wraps and any
   * recovery code, so Rhema support can never help recover this person's history. Off, this
   * device wraps its keys to the operator key again.
   */
  async setAdvancedProtection(enabled: boolean): Promise<void> {
    await this.api.setAdvancedProtection(enabled);
    this.operatorDirty = true;
    if (!enabled) await this.ensureOperatorWraps().catch(() => undefined);
  }

  /** Something about the operator wraps changed on another device or on the server. */
  escrowChanged(): Promise<number> {
    this.operatorDirty = true;
    return this.ensureOperatorWraps().catch(() => 0);
  }

  /**
   * Recovers the backup keys with a one-time recovery code from Rhema support, as on a new device
   * when every other device is gone and the backup PIN is forgotten, then reads the history.
   */
  async recoverWithCode(code: string): Promise<CodeRecovery> {
    if (!validRecoveryCode(code)) return { ok: false, reason: "invalid", lockedUntil: null };
    const none = { ok: false, reason: "none", lockedUntil: null } as const;
    let state: Awaited<ReturnType<Api["recoveryState"]>>;
    try {
      state = await this.api.recoveryState();
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return none;
      throw err;
    }
    if (state.locked_until) return { ok: false, reason: "locked", lockedUntil: state.locked_until };
    const { wrapKey, verifier } = await recoveryCodeKeys(code, fromB64(state.salt));
    let res: Awaited<ReturnType<Api["useRecoveryCode"]>>;
    try {
      res = await this.api.useRecoveryCode(verifier);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return none;
      throw err;
    }
    if (!res.wraps.length)
      return res.locked_until && res.attempts === state.attempts
        ? { ok: false, reason: "locked", lockedUntil: res.locked_until }
        : { ok: false, reason: "wrong_code", lockedUntil: res.locked_until };
    await this.refreshChecks();
    const recovered: Record<string, string> = {};
    for (const wrap of res.wraps) {
      try {
        const key = await unwrapRecoveredKey(wrapKey, wrap.version, wrap.wrapped);
        if ((await keyCheck(key)) === this.checks[wrap.version]) recovered[wrap.version] = toB64(key);
      } catch {
        // Not a wrap this code opens.
      }
    }
    if (!Object.keys(recovered).length) return { ok: false, reason: "wrong_code", lockedUntil: null };
    await this.mutex.run(() => this.addKeys(recovered));
    this.ringChanged = false;
    const chats = await this.restoreBackup();
    this.scheduleBackup();
    return { ok: true, chats };
  }

  /** Remembers that the person was asked to set a backup PIN, whatever they chose. */
  async markPinAsked(): Promise<void> {
    await this.store.set("backup-pin-asked", Date.now());
  }

  // Backup PIN.
  //
  // The PIN is blinded on the device and evaluated by the server's OPRF, then scrypt turns the
  // result into the key that wraps each backup key this device holds. The server stores the
  // wraps and counts every evaluation as a try until a device proves an unwrap with a MAC over a
  // server nonce. After the fifth unproven try it makes the account wait, longer each time. The
  // PIN itself never leaves the device.

  /** Sets or changes the backup PIN. Every backup key this device holds is wrapped under it. */
  async setBackupPin(pin: string): Promise<void> {
    if (!validPin(pin)) throw new BackupPinError("Use 6 or more digits.");
    const version = await this.mutex.run(() => this.backupVersion());
    if (!version) throw new BackupPinError("This device does not have your backup key yet.");
    const ring = await this.loadRing();
    const { blind, blinded } = blindPin(pin);
    const res = await this.api.escrowEvaluate(blinded, true);
    const evaluation = res.evaluations[0];
    if (!evaluation) throw new BackupPinError("Rhema could not set your PIN. Try again.");
    const output = finishPin(pin, blind, evaluation.evaluated);
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const wrapKey = await pinWrapKey(output, salt);
    const wraps = [];
    for (const [id, keyB64] of Object.entries(ring)) {
      if (!this.checks[id]) continue;
      const key = fromB64(keyB64);
      wraps.push({
        version: id,
        salt: toB64(salt),
        wrapped: await wrapBackupKey(wrapKey, id, key),
        auth_key: await escrowAuthKey(key),
      });
    }
    // This device holds the key, so it clears any count of wrong tries first.
    await this.proveHolder().catch(() => undefined);
    await this.api.putEscrow(evaluation.key_id, wraps);
    await this.markPinAsked();
  }

  private async proveHolder(): Promise<void> {
    const status = await this.api.escrowStatus();
    const ring = await this.loadRing();
    const version = status.versions.find((v) => ring[v]);
    const held = version ? ring[version] : undefined;
    if (!version || !held) return;
    const { nonce } = await this.api.escrowNonce();
    const auth = await escrowAuthKey(fromB64(held));
    await this.api.escrowProof(version, await escrowProof(auth, nonce));
  }

  /**
   * Recovers the backup keys with the backup PIN, as on a new device when no other device is
   * left, and then reads the history from the backup.
   */
  async recoverWithPin(pin: string): Promise<PinRecovery> {
    const { blind, blinded } = blindPin(pin);
    let res: Awaited<ReturnType<Api["escrowEvaluate"]>>;
    try {
      res = await this.api.escrowEvaluate(blinded);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404)
        return { ok: false, reason: "none", lockedUntil: null };
      throw err;
    }
    if (!res.evaluations.length) return { ok: false, reason: "locked", lockedUntil: res.locked_until };
    const outputs = new Map(res.evaluations.map((e) => [e.key_id, finishPin(pin, blind, e.evaluated)]));
    await this.refreshChecks();
    const recovered: Record<string, string> = {};
    for (const wrap of res.wraps) {
      const output = outputs.get(wrap.key_id);
      if (!output) continue;
      try {
        const key = await unwrapBackupKey(
          await pinWrapKey(output, fromB64(wrap.salt)),
          wrap.version,
          wrap.wrapped,
        );
        if ((await keyCheck(key)) === this.checks[wrap.version]) recovered[wrap.version] = toB64(key);
      } catch {
        // Not this PIN, or a wrap made with another PIN before a merge.
      }
    }
    const [first, firstKey] = Object.entries(recovered)[0] ?? [];
    if (!first || !firstKey) return { ok: false, reason: "wrong_pin", lockedUntil: res.locked_until };
    if (res.nonce) {
      const auth = await escrowAuthKey(fromB64(firstKey));
      await this.api.escrowProof(first, await escrowProof(auth, res.nonce)).catch(() => undefined);
    }
    await this.mutex.run(() => this.addKeys(recovered));
    this.ringChanged = false;
    const chats = await this.restoreBackup();
    this.scheduleBackup();
    return { ok: true, chats };
  }

  // Reading messages

  decrypt(message: ServerMessage): Promise<Decrypted> {
    return this.mutex.run(async () => {
      if (message.deleted || !message.envelope) return { ok: false, reason: "deleted" } as const;
      const env = message.envelope;
      const state = await this.store.get<InboundState>(`in:${message.chat_id}:${env.session_id}`);
      if (!state) {
        await this.want(message, env);
        const lost = (await this.loadWanted()).get(env.session_id)?.lost;
        return { ok: false, reason: lost ? "lost" : "missing_key" } as const;
      }
      // The session must belong to the device the server says sent this message.
      if (
        state.senderKey !== env.sender_key ||
        !(await this.sameSender(state.senderUserId, message.sender_id)) ||
        (message.sender_device_id && state.senderDeviceId !== message.sender_device_id)
      ) {
        return { ok: false, reason: "invalid" } as const;
      }
      const inbound = InboundGroupSession.unpickle(state.pickle, this.pickleKey);
      try {
        const { plaintext, messageIndex } = inbound.decrypt(env.ciphertext);
        const seenKey = `seen:${env.session_id}:${messageIndex}`;
        const seen = await this.store.get<string>(seenKey);
        if (seen && seen !== message.id) return { ok: false, reason: "replay" } as const;
        if (!seen) await this.store.set(seenKey, message.id);
        const payload = JSON.parse(plaintext) as Partial<Payload> | null;
        if (payload?.chat_id !== message.chat_id) return { ok: false, reason: "invalid" } as const;
        // Checked before it reaches the app, so a malformed message cannot crash a screen.
        const content = parseContent(payload.content);
        if (!content) return { ok: false, reason: "malformed" } as const;
        return { ok: true, content } as const;
      } catch {
        return { ok: false, reason: "invalid" } as const;
      } finally {
        inbound.free();
      }
    });
  }

  /**
   * Whether the account that shared a session key is the one the server now says sent the
   * message. When one person's two accounts are combined, messages sent from the old one are
   * the survivor's, while keys received before keep the old id. The old id then answers as the
   * survivor. The device and its keys are checked as before.
   */
  private async sameSender(keyedBy: string, senderId: string | null): Promise<boolean> {
    if (keyedBy === senderId) return true;
    if (!senderId) return false;
    let now = this.mergedInto.get(keyedBy);
    if (now === undefined) {
      try {
        now = (await this.api.user(keyedBy)).id;
      } catch {
        now = null;
      }
      this.mergedInto.set(keyedBy, now);
    }
    return now === senderId && now !== keyedBy;
  }

  // Persistence helpers

  private async saveInbound(
    chatId: string,
    session: InboundGroupSession,
    meta: Omit<InboundState, "pickle" | "at">,
  ): Promise<void> {
    await this.store.set(`in:${chatId}:${session.sessionId()}`, {
      pickle: session.pickle(this.pickleKey),
      at: Date.now(),
      ...meta,
    });
    if (!meta.backedUp || meta.backedUp !== this.currentVersion) this.scheduleBackup();
  }

  private async loadOlmSessions(curve: string): Promise<OlmSession[]> {
    const pickles = (await this.store.get<string[]>(`olm:${curve}`)) ?? [];
    return pickles.map((p) => OlmSession.unpickle(p, this.pickleKey));
  }

  /** Keeps the most recently used session first, and at most five per device. */
  private async saveOlmSession(curve: string, session: OlmSession): Promise<void> {
    const id = session.sessionId();
    const pickles = (await this.store.get<string[]>(`olm:${curve}`)) ?? [];
    const others = pickles.filter((p) => {
      const s = OlmSession.unpickle(p, this.pickleKey);
      const same = s.sessionId() === id;
      s.free();
      return !same;
    });
    await this.store.set(`olm:${curve}`, [session.pickle(this.pickleKey), ...others].slice(0, 5));
  }
}
